import { FormWrapper } from "@ilc-technology/form-renderer/react";
import React, { useState, useReducer, useEffect } from "react";
import StyledForm from "./formsx.styles";
import { loader } from "../../web-ui/form/utils/form-styles";
import {
  ISubmissionState,
  formSubmissionReducer,
} from "~components/web-ui/form/state/form-state";
import Components from "../../components";
import { FormStatus } from "~src/interfaces/forms";
import { getHref } from "~/src/helpers/generic-helpers";
// @ts-ignore
import ReactModal from "react-modal";
import { filterPath } from "~components/web-ui-react/breadcrumbs/breadcrumbs-helper";
import { IStoryblokStory } from "~interfaces/storyblock";
import { IMarketData } from "~components/custom/infomeeting/interfaces";
// disable bots task https://hseyit.atlassian.net/browse/MK-572
// import { addGoToWebinarRegistrant } from "~components/custom/infomeeting/services/infomeeting-service";

interface IInfomeetingFormProps {
  market: string;
  eventId?: string;
  visibleFields?: Array<string>;
  onSubmissionCompleted?: () => void;
  onSubmissionError?: () => void;
  story?: IStoryblokStory;
  form_type: string;
  form_success_screen?: any;
  form_error_screen?: any;
  form_edit_submission_screen?: "SUCCESS" | "ERROR";
  send_message_custom_question_label?: string;
  full_brochure?: boolean;
  infomeeting?: IMarketData;
  campaign_name?: string;
  campaign_type?: "cbr" | "cam";
}

// Currently FormsX only supports ids without chars, so a dummy id is provided when needed.
const ensureIdIsANumber = (id?: string) => {
  if (!id || isNaN(parseInt(id, 10))) return "1000";
  return id;
};

/**
 * Adds an event listener to the rendered form that sends a tracking event when the user first interacts with the form
 */
const addFormStartEventListeners = (form: HTMLFormElement) => {
  const addEvtLst = () => {
    dataLayer.push({ event: "formx.started" });
    form.removeEventListener("change", addEvtLst);
  };

  form.addEventListener("change", addEvtLst);
};

const labelAsPlaceholder = (fields: any, fieldsToAdapt: Array<any>) => {
  return fields.map((field: any) => {
    if (field.name && fieldsToAdapt.includes(field.name)) {
      field.placeholder = field.label;
      field.label = "";
      field.additionalData = field.additionalDetails ?? field.additionalData; // field name discrepancy in kr
    }
    return field;
  });
};

// TODO: Bad implementation, we should look for another fix on this.
// MK-535
const fixLabelForMarkets = (fields: any) => {
  return fields.map((field: any) => {
    if (field.name == "birthDate") {
      if (field?.additionalData?.labels) {
        field.additionalData.labels = {
          ...field.additionalData.labels,
          day: field.additionalData.labels.year,
          year: field.additionalData.labels.day,
        };
      }
    }
    return field;
  });
};

const FormsX = (props: IInfomeetingFormProps) => {
  const {
    form_type,
    eventId,
    onSubmissionCompleted,
    onSubmissionError = null,
    form_success_screen,
    form_error_screen,
    form_edit_submission_screen,
    send_message_custom_question_label,
    full_brochure = false,
    // infomeeting,
    campaign_name = "",
    campaign_type = "cbr",
  } = props;
  const [loading, setLoading] = useState(true);
  let parsedFormType = form_type === "campaign" ? "campaign-v2" : form_type;
  // TODO: code to fix FORMSX issues
  // https://hseyit.atlassian.net/browse/MK-573 - Link to issue
  // This is bad implementation/procedure and should not be done here
  // due to lack of alternatives this is a temporary fix to a bigger issue.
  switch (form_type) {
    case "campaign":
      parsedFormType = "campaign-v2-hsey";
      break;
    case "brochure":
      parsedFormType = "brochure-hsey";
      break;
    case "infomeeting":
      parsedFormType = "infomeeting-hsey";
      break;
    default:
      break;
  }
  const initialModalState: ISubmissionState = {
    modalIsOpen: false,
    status: FormStatus.NOT_SUBMITTED,
  };
  const [modalState, modelStateDispatch] = useReducer(
    formSubmissionReducer,
    initialModalState
  );

  // @ts-ignore
  const market: string = props.market || props.story?.lang;
  let convertedMarket: string = "";

  const updatedInfomeetingId = ensureIdIsANumber(eventId);

  const filterFields = (
    fieldsArr: Array<string>,
    filterArr: Array<string>,
    include = false
  ) =>
    fieldsArr.filter((e: any) =>
      include ? filterArr.includes(e.name) : !filterArr.includes(e.name)
    );

  const adaptConfiguration = (type: string, form: any) => {

    if (market === "beb") {
      form.formConfiguration.fields = filterFields(form.formConfiguration.fields, ['countryCode'], false);
    }

    if (
      (type === "brochure" ||
        type === "brochure-hsey" ||
        type === "infomeeting" ||
        type === "infomeeting-hsey") &&
      !full_brochure
    ) {
      let fieldsToInclude = [
        "firstName",
        "lastName",
        "email",
        "mobilePhone",
        "birthDate",
        "checkboxCombined",
      ];
      // Korean market needs to have the area code with the mobile
      if (market === "krs") {
        fieldsToInclude = [
          "firstName",
          "lastName",
          "email",
          "areaCodeMobilePhone",
          "mobilePhone",
          "birthDate",
          "checkboxCombined",
          "termConditionText",
          "hasAcceptedTerms",
          "privacyPolicyText",
          "hasAcceptedPrivacyPolicy",
          "yesEmailMarketing",
        ];
        form.formConfiguration.fields = labelAsPlaceholder(
          form.formConfiguration.fields,
          ["termConditionText", "privacyPolicyText"]
        );
      } else if (market === "frp") {
        fieldsToInclude = [
          "firstName",
          "lastName",
          "email",
          "mobilePhone",
          "postalCode",
          "birthDate",
          "checkboxCombined",
        ];
      }

      // TODO: Lable fix year/day inverted for taiwan and korean
      // this implementation has to be fixed properly somewere else.
      // MK-535
      if (market === "twi" || market === "krs") {
        form.formConfiguration.fields = fixLabelForMarkets(
          form.formConfiguration.fields
        );
      }
      return filterFields(form.formConfiguration.fields, fieldsToInclude, true);
    }

    if ((type === "brochure" || type === "brochure-hsey") && full_brochure) {
      const fieldsToExclude = [
        "phoneRadio",
        "homePhone",
        "userSelectedSourceCode",
        "comments",
        "wantsToTravelSoonTitle",
        "wantsToTravelSoon",
        "wantsToTravelSoonDescription",
        "diffCountryCode",
        "findAddress",
        "areaCodeHomePhone",
        "preferredOffice",
      ];
      form.formConfiguration.fields = labelAsPlaceholder(
        form.formConfiguration.fields,
        ["termConditionText", "privacyPolicyText"]
      );
      form.formConfiguration.fields.map((field: any) => {
        if (field?.name === "deliveryPreference") {
          // Not the best implementation
          // @todo refactor this implementation
          field.defaultValue = "e-B";
          // Ensure e-brochure appears first
          field.options = field.options.reduce(
            (
              acc: Array<{ code: string; text: string }>,
              element: { code: string; text: string }
            ) => {
              if (element.code === "e-B") {
                return [element, ...acc];
              }
              return [...acc, element];
            },
            []
          );
          // Hide Postcode field for DE
          if (market === "deb") {
            field?.businessRules.map((rule: any) => {
              if (rule.action === "conditionalShow") {
                rule.target = `${rule.target}|F-B:postalCode`;
              }
              return field;
            });
          }
          // TODO: Lable fix year/day inverted for taiwan and korean
          // this implementation has to be fixed properly somewere else.
          // MK-535
          if (market === "twi" || market === "krs") {
            form.formConfiguration.fields = fixLabelForMarkets(
              form.formConfiguration.fields
            );
          }
        }
        return field;
      });

      return filterFields(
        form.formConfiguration.fields,
        fieldsToExclude,
        false
      );
    }

    if (type === "campaign-v2" || type === "campaign-v2-hsey") {
      let fieldsToInclude = [
        "firstName",
        "lastName",
        "email",
        "birthDate",
        "mobilePhone",
        "checkboxCombined",
        "wantsBrochure",
      ];
      if (market === "frp") {
        fieldsToInclude = [
          "firstName",
          "lastName",
          "email",
          "birthDate",
          "mobilePhone",
          "postalCode",
          "mobilePhone",
          "checkboxCombined",
          "wantsBrochure",
        ];
      } else if (market === "noo") {
        fieldsToInclude = [
          "firstName",
          "lastName",
          "email",
          "birthDate",
          "mobilePhone",
          "checkboxCombined",
          "wantsBrochure",
        ];
      }
      const customField = {
        name: "question",
        autocompleteValue: null,
        defaultValue: null,
        group: "name",
        label: send_message_custom_question_label,
        type: "text",
        validationRules: [], // TODO: removed validation for now, this must be implemented other way as it is hardcoded.
      };
      form.formConfiguration.fields.map((field: any) => {
        if (field?.name === "birthDate" && market === "noo") {
          // Always show mobilePhone
          field?.businessRules.map((rule: any) => {
            if (rule.action === "ageConditionalShow") {
              rule.target = rule.target.replace("|>=:18:mobilePhone", "");
            }
            return field;
          });
        }
        return field;
      });
      const fields = [
        ...filterFields(form.formConfiguration.fields, fieldsToInclude, true),
      ];
      // @ts-ignore
      fields.splice(4, 0, customField);
      return fields;
    }
    return form;
  };

  // Enables the preview screens during Storyblok editing
  useEffect(() => {
    if (getHref().includes("editor")) {
      switch (form_edit_submission_screen) {
        case "SUCCESS":
          modelStateDispatch({ type: "OPEN_SUCCESS" });
          break;
        case "ERROR":
          // TODO: Fix for when submission fails on components that
          // do not lift a modal like infomeeting-new.
          if (onSubmissionError) {
            onSubmissionError();
          }
          modelStateDispatch({ type: "OPEN_ERROR" });
          break;
        default:
          modelStateDispatch({ type: "CLOSE" });
          break;
      }
    }
  }, [form_edit_submission_screen]);

  // Assign the formsX german language code for the Swiss market
  // tslint:disable-next-line:prefer-conditional-expression
  if (market?.toLowerCase() === "chd") {
    convertedMarket = "cs";
  } else if (market?.toLowerCase() === "bna"){
    convertedMarket = "bx";
  } else {
    // Ensures the market always respects the 2 char standard for FormsX
    // Example: SalesForce Dutch market 'nla' becomes 'nl'
    convertedMarket = market?.substr(0, 2);
  }

  const getPlugins = (story: IStoryblokStory) => {
    const slug = filterPath(story)
      .filter((e) => e !== "highschool")
      .filter((e) => e !== parsedFormType);

    const plugins = {
      tracking: {
        firstLevel: "highschool",
        secondLevel: `${slug.join("/")}${parsedFormType}`,
        thankYouPageName: "thankyou",
      },
      legacySettings: {
        productCode: "HSY",
        programCode: "HSY",
      },
    };

    switch (parsedFormType) {
      case "campaign-v2":
      case "campaign-v2-hsey":
        return {
          ...plugins,
          legacySettings: {
            ...plugins.legacySettings,
            campaignAllocationPrograms: "HSY",
          },
        };
      case "brochure":
      case "brochure-hsey":
      default:
        return plugins;
    }
  };

  return (
    <>
      <StyledForm>
        {loading && (
          <span style={{ width: "150px", height: "150px" }} css={loader} />
        )}
        <FormWrapper
          id={parsedFormType}
          formWrapperId={parsedFormType}
          marketCode={convertedMarket}
          formWrapperCSSClass={`ef-rhps-form`}
          onFormView={(form: any) =>
            (parsedFormType === "infomeeting" ||
              parsedFormType === "infomeeting-hsey") &&
            form.setInfomeetingId(updatedInfomeetingId)
          }
          plugins={props.story ? getPlugins(props.story) : {}}
          onConfigurationDownloaded={(form: any) => {
            // Remove unnecessary fields, ideally the form would not provide these for HSY
            form.formConfiguration.fields = adaptConfiguration(
              parsedFormType,
              form
            );
            // Remove unwanted extensions
            form.formConfiguration.scriptFiles = form.formConfiguration.scriptFiles.filter(
              (e: any) =>
                (e.pluginName && !e.pluginName.includes("google")) ||
                (e.pluginPath && !e.pluginPath.includes("google"))
            );
          }}
          onFormRendered={(form) => {
            addFormStartEventListeners(form.htmlElement as HTMLFormElement);
            setLoading(false);

            // Trigger TBC/wantsBrochure for campaign form
            if (
              parsedFormType === "campaign-v2" ||
              parsedFormType === "campaign-v2-hsey"
            ) {
              const wantsBrochureWrapper: HTMLElement = document.querySelector(
                ".ef-form-wantsBrochure-wrapper"
              ) as HTMLElement;
              const wantsBrochureInput: HTMLElement = document.querySelector(
                ".ef-form-wantsBrochure-wrapper input"
              ) as HTMLElement;
              if (wantsBrochureWrapper && wantsBrochureInput) {
                if (campaign_type === "cbr") {
                  wantsBrochureInput.click();
                }
                wantsBrochureWrapper.style.display = "none";
              }
            }
          }}
          onPostSubmissionObjectReady={(form: any) => {
            if (
              parsedFormType === "brochure-hsey" ||
              parsedFormType === "brochure" ||
              parsedFormType === "infomeeting" ||
              parsedFormType === "infomeeting-hsey"
            ) {
              form.data.internalData.PhoneType = "MP";
            }
            if (
              parsedFormType === "infomeeting" ||
              parsedFormType === "infomeeting-hsey"
            ) {
              form.data.InfomeetingData.Attendees = "1";
            }
            if (
              parsedFormType === "campaign-v2" ||
              parsedFormType === "campaign-v2-hsey"
            ) {
              form.data.CampaignData.CampaignAllocationPrograms = "HSY";
              // Grab question from question field
              form.data.customer.Comments = form?.fields[4]?.value || "";
              form.data.CampaignData.CampaignName =
                campaign_name || props.story?.slug;
            }
          }}
          onSubmissionCompleted={() => {
            dataLayer.push({ event: "formx.submitted" });
            if (onSubmissionCompleted) {
              onSubmissionCompleted();
            } else {
              modelStateDispatch({ type: "OPEN_SUCCESS" });
            }
          }}
        />
      </StyledForm>

      <ReactModal
        className="ef-modal -l"
        isOpen={modalState.modalIsOpen}
        onRequestClose={() => modelStateDispatch({ type: "CLOSE" })}
        overlayClassName={{
          afterOpen: "ef-modal__overlay--after-open",
          base: "ef-modal__overlay -lightbox",
          beforeClose: "ef-modal__overlay--before-close",
        }}
        htmlOpenClassName="ef-modal--active"
        ariaHideApp={false}
        closeTimeoutMS={150}
      >
        <button
          className="ef-modal__dismiss"
          onClick={() => modelStateDispatch({ type: "CLOSE" })}
        />

        {modalState.modalIsOpen &&
          modalState.status === "SUCCESS" &&
          form_success_screen?.map((blok: any) => Components(blok))}

        {modalState.modalIsOpen &&
          modalState.status === "ERROR" &&
          form_error_screen?.map((blok: any) => Components(blok))}
      </ReactModal>
    </>
  );
};

export default FormsX;
